import { Clock, FolderOpen } from 'lucide-react';
import React from 'react';
import { format, parseISO } from 'date-fns';
import { GlobalCalendarEvent, EVENT_TYPE_COLORS, EVENT_TYPE_LABELS } from '../../types/calendar';

interface DayViewProps {
  date: Date;
  events: GlobalCalendarEvent[];
  onEventClick: (event: GlobalCalendarEvent) => void;
  onDateClick?: (date: Date) => void;
  isLoading?: boolean;
}

const DayView: React.FC<DayViewProps> = ({ date, events, onEventClick, isLoading }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEventsForHour = (hour: number) => {
    return events.filter(event => {
      const start = parseISO(event.startDate);
      return start.toDateString() === date.toDateString() && start.getHours() === hour;
    });
  };

  // All-day events
  const allDayEvents = events.filter(event => {
    const start = parseISO(event.startDate);
    return event.allDay && start.toDateString() === date.toDateString();
  });

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-start border-b border-gray-100 py-2">
            <div className="w-16 h-4 bg-gray-200 rounded" />
            <div className="flex-1 pl-4 h-12 bg-gray-100 rounded ml-2" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* All-day events section */}
      {allDayEvents.length > 0 && (
        <div className="border-b border-gray-200 pb-3 mb-3">
          <div className="text-xs font-medium text-gray-500 mb-2">All day</div>
          <div className="space-y-1">
            {allDayEvents.map(event => {
              const colors = EVENT_TYPE_COLORS[event.type];
              return (
                <button
                  key={event.id}
                  onClick={() => onEventClick(event)}
                  className={`w-full text-left p-2 ${colors.bg} ${colors.text} border ${colors.border} rounded-lg hover:opacity-80 transition-opacity`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
                    <span className="font-medium text-sm">{event.title}</span>
                    <span className="text-xs opacity-75">({EVENT_TYPE_LABELS[event.type]})</span>
                  </div>
                  <div className="text-xs opacity-75 mt-0.5 pl-[18px]">{event.projectName}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Hourly slots */}
      {hours.map(hour => {
        const hourEvents = getEventsForHour(hour);

        return (
          <div key={hour} className="flex items-start border-b border-gray-100 py-2">
            <div className="w-16 text-sm text-gray-600 pt-1">
              {String(hour).padStart(2, '0')}:00
            </div>

            <div className="flex-1 pl-4">
              {hourEvents.length === 0 ? (
                <div className="h-12" />
              ) : (
                <div className="space-y-1">
                  {hourEvents.map(event => {
                    const colors = EVENT_TYPE_COLORS[event.type];
                    return (
                      <button
                        key={event.id}
                        onClick={() => onEventClick(event)}
                        className={`w-full text-left p-3 ${colors.bg} border ${colors.border} rounded-lg hover:opacity-80 transition-opacity`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className={`font-medium ${colors.text}`}>{event.title}</div>
                            <div
                              className={`flex items-center space-x-3 text-sm mt-1 ${colors.text} opacity-75`}
                            >
                              <div className="flex items-center space-x-1">
                                <Clock className="h-4 w-4" />
                                <span>
                                  {format(parseISO(event.startDate), 'HH:mm')} -{' '}
                                  {format(parseISO(event.endDate), 'HH:mm')}
                                </span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <FolderOpen className="h-4 w-4" />
                                <span>{event.projectName}</span>
                              </div>
                            </div>
                          </div>
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}
                          >
                            {EVENT_TYPE_LABELS[event.type]}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default DayView;
